/**
 * Xamun Owner admin — invitation codes (Gap 3 Path A).
 *
 * Mint a code bound to an XI company (default) or as PlayStudio-only.
 * List existing codes, see usage, revoke. Every action is audit-logged
 * on the API side.
 */

import { useState } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { ApiError} from "../../api/client";
import { apiGet, apiPost } from "../../api/client";
import { AdminShell } from "../../components/admin/AdminShell";

interface InvitationCode {
  code: string;
  label: string;
  companyId: string | null;
  playstudioOnly: boolean;
  workspaceNameDefault: string | null;
  cxoRoleTagDefault: string;
  maxUses: number | null;
  usedCount: number;
  expiresAt: string | null;
  revokedAt: string | null;
  createdBy: string;
  createdAt: string;
}

export function AdminInvitationCodes() {
  const qc = useQueryClient();
  const [formOpen, setFormOpen] = useState(false);

  const listQ = useQuery<{ codes: InvitationCode[] }, ApiError>({
    queryKey: ["admin-invitation-codes"],
    queryFn: () => apiGet("/api/admin/invitation-codes"),
    retry: false,
  });

  const revokeM = useMutation<unknown, ApiError, string>({
    mutationFn: (code) =>
      apiPost(`/api/admin/invitation-codes/${encodeURIComponent(code)}/revoke`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-invitation-codes"] }),
  });

  return (
    <AdminShell>
    <main className="review-page" style={{ margin: "0 auto" }}>
      <p className="review-eyebrow">xamun owner</p>
      <h1 className="review-title">Invitation codes</h1>
      <p className="review-meta">
        Mint codes for new tenants. Each code is bound to an XI company by
        default — the redeemer's workspace gets provisioned under that company
        when they sign in via OIDC.
      </p>

      {listQ.error?.status === 403 && (
        <div className="review-banner error" style={{ marginTop: 16 }}>
          You're not on the Xamun admin allow-list.
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        {formOpen ? (
          <MintForm
            onCancel={() => setFormOpen(false)}
            onCreated={async () => {
              setFormOpen(false);
              await qc.invalidateQueries({ queryKey: ["admin-invitation-codes"] });
            }}
          />
        ) : (
          <button
            type="button"
            className="review-btn approve"
            onClick={() => setFormOpen(true)}
          >
            + Mint code
          </button>
        )}
      </div>

      <section className="review-section">
        <h2 className="review-section-title">
          {listQ.data ? `${listQ.data.codes.length} codes` : "Loading…"}
        </h2>
        {listQ.isLoading ? (
          <div style={{ color: "var(--text-3)" }}>Loading…</div>
        ) : !listQ.data || listQ.data.codes.length === 0 ? (
          <div className="ho-empty">No codes minted yet.</div>
        ) : (
          <ul className="review-comment-list">
            {listQ.data.codes.map((c) => {
              const isRevoked = c.revokedAt !== null;
              const isExpired =
                c.expiresAt !== null &&
                new Date(c.expiresAt).getTime() < Date.now();
              const isUsedUp =
                c.maxUses !== null && c.usedCount >= c.maxUses;
              const isActive = !isRevoked && !isExpired && !isUsedUp;
              return (
                <li key={c.code} className="review-comment">
                  <div className="review-comment-head">
                    <code
                      className="author"
                      style={{ fontFamily: "monospace", fontSize: 13 }}
                    >
                      {c.code}
                    </code>
                    {isActive ? (
                      <span className="review-comment-decision approve">active</span>
                    ) : isRevoked ? (
                      <span className="review-comment-decision return">revoked</span>
                    ) : isExpired ? (
                      <span className="review-comment-decision return">expired</span>
                    ) : (
                      <span className="review-comment-decision note">used up</span>
                    )}
                    {c.playstudioOnly && (
                      <span
                        className="review-comment-decision note"
                        title="No XI company binding"
                      >
                        playstudio-only
                      </span>
                    )}
                    <span style={{ marginLeft: "auto", fontSize: 11 }}>
                      {new Date(c.createdAt).toLocaleDateString()}
                    </span>
                  </div>

                  <div style={{ marginTop: 6, fontSize: 13, color: "var(--text-2, #475569)" }}>
                    <strong>{c.label}</strong>
                    {c.companyId && (
                      <> · company <code>{c.companyId}</code></>
                    )}
                    {c.workspaceNameDefault && (
                      <> · default name "{c.workspaceNameDefault}"</>
                    )}
                  </div>

                  <div style={{ marginTop: 4, fontSize: 12, color: "var(--text-3, #64748b)" }}>
                    <strong>{c.usedCount}</strong>
                    {c.maxUses != null ? ` / ${c.maxUses}` : ""} used
                    {c.expiresAt && (
                      <> · expires {new Date(c.expiresAt).toLocaleDateString()}</>
                    )}
                    {" · "}role: {c.cxoRoleTagDefault}
                  </div>

                  {isActive && (
                    <div style={{ marginTop: 8 }}>
                      <CopyableUrl code={c.code} />
                      <button
                        type="button"
                        className="review-btn note"
                        style={{ marginLeft: 8, minWidth: 100 }}
                        disabled={revokeM.isPending}
                        onClick={() => {
                          if (confirm(`Revoke code ${c.code}? This is permanent.`)) {
                            revokeM.mutate(c.code);
                          }
                        }}
                      >
                        Revoke
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </main>
    </AdminShell>
  );
}

function CopyableUrl({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const base = typeof window !== "undefined" ? window.location.origin : "";
  // Codes redeem from the empty-state on /post-login. The reviewer can also
  // hand the recipient the code itself to type — pasting the URL just routes
  // them through OIDC first which lands on /post-login anyway.
  const text = code;
  return (
    <button
      type="button"
      className="review-btn note"
      style={{ minWidth: 90, fontFamily: "monospace", fontSize: 11 }}
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* clipboard may be blocked — surface silently */
        }
      }}
      title={`${base}/post-login (enter code: ${code})`}
    >
      {copied ? "✓ copied" : "Copy code"}
    </button>
  );
}

function MintForm({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: () => void;
}) {
  const [label, setLabel] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [playstudioOnly, setPlaystudioOnly] = useState(false);
  const [workspaceNameDefault, setWorkspaceNameDefault] = useState("");
  const [cxoRoleTagDefault, setCxoRoleTagDefault] = useState("cxo");
  const [ttlDays, setTtlDays] = useState("30");
  const [maxUses, setMaxUses] = useState("1");

  const mint = useMutation<{ code: string }, ApiError, void>({
    mutationFn: () =>
      apiPost("/api/admin/invitation-codes", {
        label: label.trim(),
        ...(companyId.trim() && { company_id: companyId.trim() }),
        ...(playstudioOnly && { playstudio_only: true }),
        ...(workspaceNameDefault.trim() && {
          workspace_name_default: workspaceNameDefault.trim(),
        }),
        ...(cxoRoleTagDefault.trim() && {
          cxo_role_tag_default: cxoRoleTagDefault.trim(),
        }),
        ...(ttlDays && Number(ttlDays) > 0 && { ttl_days: Number(ttlDays) }),
        ...(maxUses && Number(maxUses) > 0 && { max_uses: Number(maxUses) }),
      }),
    onSuccess: onCreated,
  });

  const canSubmit =
    label.trim().length >= 2 &&
    (companyId.trim().length > 0 || playstudioOnly) &&
    !mint.isPending;

  return (
    <section className="review-form" style={{ marginTop: 12 }}>
      <h2 className="review-section-title" style={{ marginBottom: 14 }}>
        Mint invitation code
      </h2>

      <label className="review-field">
        <span className="review-field-label">
          <strong>Label</strong>
          <span className="review-field-hint">internal note — e.g. "Acme pilot 2026-Q4"</span>
        </span>
        <input
          className="review-input"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
        />
      </label>

      <label className="review-field">
        <span className="review-field-label">
          <strong>XI company id</strong>
          <span className="review-field-hint">
            required unless this is a PlayStudio-only tenant
          </span>
        </span>
        <input
          className="review-input"
          value={companyId}
          onChange={(e) => {
            setCompanyId(e.target.value);
            if (e.target.value.trim()) setPlaystudioOnly(false);
          }}
          placeholder="co_acme_corp"
          disabled={playstudioOnly}
        />
      </label>

      <label
        className="review-field"
        style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}
      >
        <input
          type="checkbox"
          checked={playstudioOnly}
          onChange={(e) => {
            setPlaystudioOnly(e.target.checked);
            if (e.target.checked) setCompanyId("");
          }}
          style={{ width: "auto" }}
        />
        <span style={{ fontSize: 13 }}>
          <strong>PlayStudio-only tenant</strong>{" "}
          <span style={{ color: "var(--text-3, #64748b)" }}>
            — generates a co_pso_* id
          </span>
        </span>
      </label>

      <div className="spotted-form-row" style={{ marginTop: 12 }}>
        <label className="spotted-field">
          <span>Default workspace name (optional)</span>
          <input
            className="review-input"
            value={workspaceNameDefault}
            onChange={(e) => setWorkspaceNameDefault(e.target.value)}
            placeholder="ACME · COO"
          />
        </label>
        <label className="spotted-field">
          <span>CXO role tag</span>
          <input
            className="review-input"
            value={cxoRoleTagDefault}
            onChange={(e) => setCxoRoleTagDefault(e.target.value)}
          />
        </label>
      </div>

      <div className="spotted-form-row" style={{ marginTop: 12 }}>
        <label className="spotted-field">
          <span>TTL (days)</span>
          <input
            className="review-input"
            type="number"
            min={1}
            value={ttlDays}
            onChange={(e) => setTtlDays(e.target.value)}
          />
        </label>
        <label className="spotted-field">
          <span>Max uses</span>
          <input
            className="review-input"
            type="number"
            min={1}
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
          />
        </label>
      </div>

      {mint.error && (
        <div className="review-banner error" style={{ marginTop: 12 }}>
          {mint.error.status === 400
            ? "Provide either an XI company_id or check PlayStudio-only."
            : `Could not mint (${mint.error.status}).`}
        </div>
      )}

      <div className="review-actions" style={{ marginTop: 16 }}>
        <button
          type="button"
          className="review-btn approve"
          disabled={!canSubmit}
          onClick={() => mint.mutate()}
        >
          {mint.isPending ? "Minting…" : "Mint"}
        </button>
        <button type="button" className="review-btn note" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </section>
  );
}
